<template>
  <Component
    :is="overlay"
    v-if="overlay"
    :key="overlayKey"
    v-bind="overlayProps"
    @closed="handleClosed"
  />
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import type { Component } from 'vue';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import OAuthSettingsDrawer from '@/components/integrations/OAuthSettingsDrawer.vue';

import type { ApiTabValueType } from '../types';
import useApiRoutes from '../useApiRoutes';
import useApiTabs from '../useApiTabs';

import ApiFilterDrawer from './ApiFilterDrawer.vue';
import ApiIntegrationCreateModal from './ApiIntegrationCreateModal.vue';
import ApiKeyCreateModal from './ApiKeyCreateModal.vue';
import ApiWebhookCreateModal from './ApiWebhookCreateModal.vue';
import ApiWebhookSettingsDrawer from './ApiWebhookSettingsDrawer.vue';

const CREATE_COMPONENTS: Partial<Record<ApiTabValueType, Component>> = {
  webhooks: ApiWebhookCreateModal,
  integrations: ApiIntegrationCreateModal,
  filters: ApiFilterDrawer,
  keys: ApiKeyCreateModal,
};

const DETAIL_COMPONENTS: Partial<Record<ApiTabValueType, Component>> = {
  webhooks: ApiWebhookSettingsDrawer,
  integrations: OAuthSettingsDrawer,
  filters: ApiFilterDrawer,
};

const route = useRoute();
const { getTabByValue } = useApiTabs();
const { closeOverlay } = useApiRoutes();

const currentTab = computed(() => getTabByValue(route.params.tab as string));
const resourceId = computed(() => (route.params.id as string) ?? '');
const overlay = computed(() => {
  if (!currentTab.value) return undefined;
  const components = resourceId.value ? DETAIL_COMPONENTS : CREATE_COMPONENTS;
  return components[currentTab.value.value];
});

const isEditedInPlace = computed(
  () =>
    !!currentTab.value &&
    CREATE_COMPONENTS[currentTab.value.value] ===
      DETAIL_COMPONENTS[currentTab.value.value],
);
const overlayKey = computed(() =>
  isEditedInPlace.value ? currentTab.value?.value : resourceId.value,
);
const overlayProps = computed(() =>
  resourceId.value ? { resourceId: resourceId.value } : {},
);

function handleClosed() {
  if (currentTab.value) closeOverlay(currentTab.value.value);
}

onMounted(() => {
  if (!overlay.value) handleClosed();
});

useHead({
  title: computed(() =>
    resourceId.value
      ? `${currentTab.value?.name} - API - Crossbeam`
      : `Create - ${currentTab.value?.name} - API - Crossbeam`,
  ),
});
</script>
